<script setup lang="ts">
import type { PanelMenuPassThroughOptions } from 'primevue/panelmenu';
import type { RouteRecordName } from 'vue-router';
import {
  computed,
  ref,
  watch,
} from 'vue';
import {
  useRoute,
  useRouter,
} from 'vue-router';

import type { PrimeVueMenuItem } from '@modules/sidebar/types';
import { AFFILIATES_PENDING_LIST_ROUTE_NAME } from '@modules/affiliates-pending/constants';
import { useAffiliatesPendingCountQuery } from '@modules/affiliates-pending/queries';
import {
  ADMIN_ROUTE_NAME,
  AFFILIATE_ROUTE_NAME,
} from '@modules/core/services/router/constants';
import { ADMIN_GENERAL_SETTINGS_ROUTE_NAME } from '@modules/general-settings/constants';
import TheMerchantLogo from '@modules/merchant/components/TheMerchantLogo/TheMerchantLogo.vue';
import { useAddMerchantIdToRoute } from '@modules/merchant/composables';
import NewAppVersion from '@modules/new-app-version/components/NewAppVersion/NewAppVersion.vue';
import { useCurrentNamespace } from '@modules/permissions/composables';
import { usePermissionsStore } from '@modules/permissions/store';
import { useAppMenu } from '@modules/sidebar/composables';
import { isString } from '@utils/guards';

const { isAdminNamespace, isAffiliateNamespace } = useCurrentNamespace();

const appMenu = useAppMenu();
const route = useRoute();
const router = useRouter();

const { mixMerchantIdIntoRoute } = useAddMerchantIdToRoute();
const { hasPermission } = usePermissionsStore();

const { data: pendingCount } = useAffiliatesPendingCountQuery();

const menuItems = computed<PrimeVueMenuItem[]>(() => {
  if (isAdminNamespace.value) {
    return appMenu.getAdminMenu().value.map((item) => rebuildMenuItemsToAddMerchantId(item));
  }

  if (isAffiliateNamespace.value) {
    return appMenu.getAffiliateMenu().value.map((item) => rebuildMenuItemsToAddMerchantId(item));
  }

  return [];
});

const expandedKeys = ref<Record<string, boolean>>({});

const matchedPaths = computed<RouteRecordName[]>(() => (
  route.matched.map((matched) => matched.name).filter((item) => (
    !!item && item !== ADMIN_ROUTE_NAME && item !== AFFILIATE_ROUTE_NAME
  ))
));

const panelMenuPt: PanelMenuPassThroughOptions = {
  root: { class: 'gap-0' },
  contentContainer: { class: ' p-0' },
  panel: { class: '!shadow-none my-0 border-0' },
  rootList: { class: 'pl-0' },
};

function rebuildMenuItemsToAddMerchantId(item: PrimeVueMenuItem): PrimeVueMenuItem {
  if (item.items) {
    return {
      ...item,
      items: item.items.map((val) => mixMerchantIdIntoRoute(val)),
    };
  }
  return mixMerchantIdIntoRoute(item);
}

function isActiveRoute(item: PrimeVueMenuItem, matched?: RouteRecordName[]): boolean {
  if (!item.route) {
    return false;
  }

  const { name, matched: routeMatched } = router.resolve(item.route);

  if (!name) {
    return false;
  }

  let routeMatchedNames = routeMatched.map((routeItem) => routeItem.name);

  // TODO: Delete this after removing tabs from General settings and making those pages stand-alone
  if (routeMatchedNames.includes(ADMIN_GENERAL_SETTINGS_ROUTE_NAME)) {
    routeMatchedNames = routeMatchedNames.slice(-1);
  }

  return !!matched?.some((routeName) => routeMatchedNames.includes(routeName));
}

function initExpandedKeys(newPath: RouteRecordName[], oldPath?: RouteRecordName[]): void {
  const newExpandedKeys: Record<string, boolean> = {};

  for (const item of menuItems.value) {
    if (!item.key) {
      continue;
    }

    const isExpanded = isActiveRoute(item, newPath) || !!item.items?.some((subItem) => isActiveRoute(subItem, newPath));

    // Do not change on top level route click
    if (isExpanded && item.level === 0 && !item.items?.length) {
      return;
    }

    const isMatchedOld = isActiveRoute(item, oldPath) || !!item.items?.some((subItem) => isActiveRoute(subItem, oldPath));

    if (isExpanded && isMatchedOld) {
      newExpandedKeys[item.key] = expandedKeys.value[item.key];
      continue;
    }

    newExpandedKeys[item.key] = isExpanded;
  }

  expandedKeys.value = newExpandedKeys;
}

function isActive(item: PrimeVueMenuItem): boolean {
  if (!item.key) {
    return false;
  }

  if (!item.items?.length) {
    return isActiveRoute(item, matchedPaths.value);
  }

  return expandedKeys.value[item.key];
}

function getItemListClass(item: PrimeVueMenuItem, isActiveLink = false): string {
  let classList = 'flex items-center gap-2 pr-4 rounded-lg';
  const isActiveItem = isActive(item) || isActiveLink;

  if (item.level === 0) {
    classList += ' py-4 pl-4 font-medium';
    if (isActiveItem) {
      classList += ' bg-surface-200';
    }
  } else {
    classList += ' py-3 pl-6';
  }

  if (isActiveItem) {
    classList += ' text-primary font-medium';
  } else {
    classList += ' text-muted-color';
  }

  return classList;
}

function onPanelOpen({ item }: { item: PrimeVueMenuItem }): void {
  if (!item.items?.length) {
    return;
  }

  const newExpandedKeys: Record<string, boolean> = {};

  for (const key of Object.keys(expandedKeys.value)) {
    newExpandedKeys[key] = false;
  }

  if (item.key) {
    newExpandedKeys[item.key] = true;
  }

  expandedKeys.value = newExpandedKeys;
}

function isItemVisible(item: PrimeVueMenuItem, hasSubmenu?: boolean): boolean {
  if (hasSubmenu && item.items) {
    return item.items.some((subItem) => (subItem.permission ? hasPermission(subItem.permission).value : true));
  }

  return item.permission ? hasPermission(item.permission).value : true;
}

watch(matchedPaths, (newValue, oldValue) => {
  initExpandedKeys(newValue, oldValue);
}, { immediate: true, deep: true });
</script>

<template>
  <div v-data-qa-auto-id="'VSidebarMenu'" class="sticky top-0 z-10">
    <TheMerchantLogo />
  </div>
  <PPanelMenu
    v-model:expanded-keys="expandedKeys"
    :model="menuItems"
    exact
    :pt="panelMenuPt"
    @panel-open="onPanelOpen"
  >
    <template #item="{ item, label, hasSubmenu }">
      <template v-if="item.route">
        <router-link
          v-if="isItemVisible(item, hasSubmenu)"
          v-slot="{ navigate, href, isActive: isActiveLink }"
          custom
          :to="item.route"
        >
          <a
            :href="href"
            :class="getItemListClass(item, isActiveLink)"
            @click="navigate"
          >
            <i
              v-if="item.icon"
              :class="item.icon"
            />
            <span>
              {{ isString(label) && $t(label) }}
            </span>

            <PBadge
              v-if="item.id === AFFILIATES_PENDING_LIST_ROUTE_NAME && pendingCount"
              :value="pendingCount"
              class="ml-auto"
            />
          </a>
        </router-link>
      </template>
      <template v-else>
        <div
          v-if="isItemVisible(item, hasSubmenu)"
          :class="getItemListClass(item)"
        >
          <i
            v-if="item.icon"
            :class="item.icon"
          />
          <span>
            {{ isString(label) && $t(label) }}
          </span>
          <i
            v-if="hasSubmenu"
            class="pi ml-auto text-muted-color"
            :class="isActive(item) ? 'pi-chevron-down' : 'pi-chevron-right'"
          />
        </div>
      </template>
    </template>
  </PPanelMenu>
  <div class="my-4 flex flex-col px-2">
    <NewAppVersion />
  </div>
</template>
